import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Coins, Zap, Loader2, Plus, Minus, Film, History, Wallet, Repeat,
  Shield, CheckCircle, XCircle, ArrowDownToLine, ArrowUpFromLine,
} from 'lucide-react';

const RATE_USD_PER_CREDIT = 0.65; // fallback; backend returns the authoritative rate

function kindMeta(kind) {
  switch (kind) {
    case 'purchase':             return { label: 'Purchase',          color: 'text-emerald-400', Icon: ArrowDownToLine };
    case 'topup':                return { label: 'Auto top-up',       color: 'text-emerald-300', Icon: Repeat };
    case 'admin':                return { label: 'Admin grant',       color: 'text-cyan-300',    Icon: Shield };
    case 'admin_revoke':         return { label: 'Admin adjustment',  color: 'text-rose-300',    Icon: Shield };
    case 'video_simple':         return { label: 'Video (Simple)',    color: 'text-violet-300',  Icon: Film };
    case 'video_story':          return { label: 'Video (Story)',     color: 'text-violet-300',  Icon: Film };
    case 'video_scene_regenerate': return { label: 'Scene regen',     color: 'text-violet-300',  Icon: Film };
    case 'video_refund_fail':    return { label: 'Refund — failed',   color: 'text-amber-300',   Icon: ArrowUpFromLine };
    case 'bonus':                return { label: 'Bonus',             color: 'text-amber-300',   Icon: CheckCircle };
    default:                     return { label: kind,                color: 'text-slate-300',   Icon: Coins };
  }
}

export default function CreditsWallet() {
  const { user, refresh } = useAuth();
  const [balance, setBalance] = useState(0);
  const [packages, setPackages] = useState([]);
  const [costs, setCosts] = useState({ items: [], rate_usd_per_credit: RATE_USD_PER_CREDIT });
  const [txns, setTxns] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [custom, setCustom] = useState('');
  const [autoTopup, setAutoTopup] = useState({ enabled: false, threshold: 10, amount: 20 });
  const [savingAt, setSavingAt] = useState(false);
  const [saveCard, setSaveCard] = useState(true);

  const load = async () => {
    const [me, pkg, cs, tx] = await Promise.all([
      api.get('/credits/me'),
      api.get('/credits/packages'),
      api.get('/credits/costs'),
      api.get('/credits/transactions'),
    ]);
    setBalance(me.data.balance);
    setAutoTopup(me.data.auto_topup || { enabled: false, threshold: 10, amount: 20 });
    setPackages(pkg.data);
    setCosts(cs.data);
    setTxns(tx.data);
  };
  useEffect(() => { if (user) load(); }, [user]);

  const buy = async (pkg_id) => {
    setLoadingPkg(pkg_id);
    try {
      const { data } = await api.post('/credits/checkout', {
        package_id: pkg_id, origin_url: window.location.origin, save_payment_method: saveCard,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setLoadingPkg(null);
    }
  };
  const buyCustom = async () => {
    const amt = Number(custom);
    if (!amt || amt < 5) return toast.error('Minimum $5');
    setLoadingPkg('custom');
    try {
      const { data } = await api.post('/credits/checkout', {
        custom_amount_usd: amt, origin_url: window.location.origin, save_payment_method: saveCard,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setLoadingPkg(null);
    }
  };

  const saveAutoTopup = async (patch) => {
    setSavingAt(true);
    try {
      const body = { ...autoTopup, ...patch };
      const { data } = await api.post('/credits/autotopup', body);
      setAutoTopup(data.auto_topup);
      toast.success('Auto top-up updated');
    } catch (e) {
      toast.error('Save failed');
    } finally { setSavingAt(false); }
  };

  const rate = costs.rate_usd_per_credit || RATE_USD_PER_CREDIT;
  const customCredits = useMemo(() => {
    const amt = Number(custom);
    if (!amt || amt < 5) return 0;
    return Math.round(amt / rate);
  }, [custom, rate]);

  if (!user) {
    return <div className="p-10 text-center text-slate-400">Please sign in to view your credits.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 py-4">
      {/* Header / balance */}
      <Card className="glass p-6 relative overflow-hidden" data-testid="credits-balance-card">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
        <div className="flex items-center justify-between gap-4 relative">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Video Credits</div>
            <div className="font-display text-5xl font-black mt-1 flex items-end gap-2">
              <Coins className="w-10 h-10 text-amber-400 -mb-1" />
              <span data-testid="credits-balance-value">{balance.toLocaleString()}</span>
              <span className="text-sm font-normal text-slate-400 mb-2">credits</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Pay-as-you-go for AI video generation. Does not expire.
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Video costs</div>
            {costs.items.slice(0, 3).map(c => (
              <div key={c.id} className="text-xs text-slate-300">{c.label} — <b>{c.cost_credits}</b> cr</div>
            ))}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="buy">
        <TabsList className="glass flex flex-wrap h-auto">
          <TabsTrigger value="buy" data-testid="credits-tab-buy"><Wallet className="w-3.5 h-3.5 mr-1" /> Buy Credits</TabsTrigger>
          <TabsTrigger value="autotopup" data-testid="credits-tab-autotopup"><Repeat className="w-3.5 h-3.5 mr-1" /> Auto Top-Up</TabsTrigger>
          <TabsTrigger value="history" data-testid="credits-tab-history"><History className="w-3.5 h-3.5 mr-1" /> History</TabsTrigger>
        </TabsList>

        {/* Buy credits */}
        <TabsContent value="buy" className="space-y-4 mt-4">
          <div>
            <h2 className="font-display text-2xl font-black">Buy Credits</h2>
            <p className="text-sm text-slate-400">One-time purchase. Pay only for what you use.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {packages.map(p => (
              <Card key={p.id} data-testid={`credit-pkg-${p.name.toLowerCase()}`}
                className={`glass p-5 relative transition-all hover:scale-[1.02] ${p.featured ? 'ring-2 ring-amber-400/70' : ''}`}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-slate-950 uppercase tracking-wider">
                    Best value
                  </div>
                )}
                <div className="text-xs uppercase tracking-wider text-slate-400">{p.name}</div>
                <div className="font-display text-4xl font-black mt-1">${p.price_usd}</div>
                <div className="mt-2 flex items-center gap-1.5 text-amber-300 font-bold">
                  <Coins className="w-4 h-4" /> {p.credits.toLocaleString()} credits
                </div>
                {p.est_videos > 0 && (
                  <div className="text-xs text-slate-500 mt-1">~{p.est_videos} videos</div>
                )}
                <Button
                  data-testid={`credit-pkg-buy-${p.id}`}
                  disabled={loadingPkg === p.id}
                  onClick={() => buy(p.id)}
                  className="w-full mt-4 gradient-brand text-white">
                  {loadingPkg === p.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> : <>Buy — ${p.price_usd}</>}
                </Button>
              </Card>
            ))}
          </div>

          {/* Custom amount */}
          <Card className="glass p-5" data-testid="credits-custom-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-display font-black text-lg">Custom amount</div>
                <div className="text-xs text-slate-400">Minimum $5. Pay what you want — same per-credit rate as the Starter pack.</div>
              </div>
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Amount (USD)</Label>
                  <Input data-testid="credits-custom-amount" type="number" min="5" step="1" value={custom} onChange={e => setCustom(e.target.value)} className="w-28" placeholder="5" />
                </div>
                <div className="pb-1 text-xs text-amber-300 font-bold min-w-[90px]">
                  {customCredits ? <>= {customCredits} cr</> : <span className="text-slate-500">— credits</span>}
                </div>
                <Button data-testid="credits-custom-buy" disabled={loadingPkg === 'custom'} onClick={buyCustom} className="gradient-brand text-white">
                  {loadingPkg === 'custom' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy'}
                </Button>
              </div>
            </div>
          </Card>

          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" data-testid="credits-save-card" checked={saveCard} onChange={e => setSaveCard(e.target.checked)} className="accent-violet-500" />
            Save payment method securely so Auto Top-Up can charge me when my balance runs low.
          </label>
          <p className="text-[11px] text-slate-500">Secure checkout.</p>
        </TabsContent>

        {/* Auto top-up */}
        <TabsContent value="autotopup" className="space-y-4 mt-4">
          <Card className="glass p-5 space-y-4" data-testid="autotopup-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-black text-lg">Auto Top-Up</div>
                <div className="text-xs text-slate-400">Automatically purchase credits when your balance runs low.</div>
              </div>
              <Switch
                data-testid="autotopup-enabled"
                checked={autoTopup.enabled}
                onCheckedChange={v => saveAutoTopup({ enabled: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">When balance reaches</Label>
                <Input data-testid="autotopup-threshold" type="number" min="0" max="500"
                  defaultValue={autoTopup.threshold}
                  onBlur={e => { const v = +e.target.value; if (v !== autoTopup.threshold) saveAutoTopup({ threshold: v }); }} />
                <div className="text-[10px] text-slate-500 mt-1">credits</div>
              </div>
              <div>
                <Label className="text-xs">Automatically purchase</Label>
                <Input data-testid="autotopup-amount" type="number" min="5" max="500"
                  defaultValue={autoTopup.amount}
                  onBlur={e => { const v = +e.target.value; if (v !== autoTopup.amount) saveAutoTopup({ amount: v }); }} />
                <div className="text-[10px] text-slate-500 mt-1">credits (charged at ~${(autoTopup.amount * rate).toFixed(2)})</div>
              </div>
            </div>

            <div className="rounded-xl p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
              <b>Heads up:</b> when enabled, your saved payment method is charged automatically. Jixxeo AI only charges once per threshold event (duplicate charges and runaway loops are blocked).
            </div>
            {!user?.default_payment_method && autoTopup.enabled && (
              <div className="rounded-xl p-3 bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="autotopup-no-card">
                No saved payment method on file. Buy any credit pack with "Save payment method" checked to enable automatic top-up charges.
              </div>
            )}
            {savingAt && <div className="text-xs text-slate-500">Saving…</div>}
          </Card>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-4">
          <Card className="glass p-2" data-testid="credits-history-list">
            {txns.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No transactions yet.</div>}
            {txns.map(t => {
              const m = kindMeta(t.kind);
              const sign = t.delta > 0 ? '+' : '';
              return (
                <div key={t.id} className="flex items-center justify-between gap-3 p-3 border-b border-white/5 text-sm">
                  <div className="flex items-center gap-3">
                    <m.Icon className={`w-5 h-5 ${m.color}`} />
                    <div>
                      <div className="font-semibold">{m.label}</div>
                      <div className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-black ${t.delta > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{sign}{t.delta}</div>
                    <div className="text-[11px] text-slate-500">bal: {t.balance_after}</div>
                  </div>
                </div>
              );
            })}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
